/**
 * useCurrentPageType - Detects the current page type for contextual layout selection
 * 
 * Returns the page type string if layouts are applicable, null otherwise.
 * Used by AppearanceHub to show contextual layout options.
 */
import { useLocation } from 'react-router-dom';

// Page type definitions with metadata
const PAGE_TYPES = {
    homepage: {
        id: 'homepage',
        label: 'Homepage',
        // Match: /, /q, /sports (sports home)
        patterns: [/^\/$/, /^\/q\/?$/, /^\/sports\/?$/]
    },
    categoryPage: {
        id: 'categoryPage',
        label: 'Category',
        // Match: /q/category/*, /:categorySlug/:topicSlug (e.g., /sports/ncaa-basketball)
        patterns: [/^\/q\/category\//, /^\/[a-z-]+\/[a-z0-9-]+\/?$/]
    },
    articleDetail: {
        id: 'articleDetail',
        label: 'Article',
        // Match: /q/articles/:slug (legacy) or /:category/:topic/:articleSlug (hierarchical)
        patterns: [/^\/q\/articles\/[a-z0-9-]+\/?$/, /^\/[a-z0-9-]+\/[a-z0-9-]+\/[a-z0-9-]+\/?$/]
    }
};

/**
 * Hook to detect current page type based on URL pathname
 * @returns {{ pageType: string|null, label: string|null }} Object with pageType id and human-readable label
 */
export default function useCurrentPageType() {
    // SSR-safe: useLocation() throws when no <Router> context exists during SSR.
    // Return a safe default so AppearanceHub can render its trigger button in SSR HTML.
    let pathname = '/';
    try {
        const location = useLocation();
        pathname = location?.pathname || '/';
    } catch {
        return { pageType: null, label: null };
    }

    // Check each page type's patterns
    for (const [typeId, config] of Object.entries(PAGE_TYPES)) {
        for (const pattern of config.patterns) {
            if (pattern.test(pathname)) {
                return {
                    pageType: config.id,
                    label: config.label
                };
            }
        }
    }

    // No layout options for this page (e.g., login, account, admin)
    return {
        pageType: null,
        label: null
    };
}

// Export page type metadata for use in components
export { PAGE_TYPES };
